import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft,
  Trash2,
  RefreshCw,
  Search,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  Users as UsersIcon,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

function StatusPill({ status }) {
  const map = {
    trialing: { label: "Trialing", cls: "bg-amber-50 text-amber-700" },
    active: { label: "Active", cls: "bg-emerald-50 text-emerald-700" },
    past_due: { label: "Past due", cls: "bg-red-50 text-red-700" },
    canceled: { label: "Canceled", cls: "bg-zinc-100 text-zinc-700" },
    unpaid: { label: "Unpaid", cls: "bg-red-50 text-red-700" },
    incomplete: { label: "Incomplete", cls: "bg-zinc-100 text-zinc-700" },
    incomplete_expired: { label: "Expired", cls: "bg-zinc-100 text-zinc-700" },
  };
  if (!status) return <span className="text-xs text-zinc-400">— no plan</span>;
  const v = map[status] || { label: status, cls: "bg-zinc-100 text-zinc-700" };
  return <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-md ${v.cls}`}>{v.label}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function AdminUsers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [confirm, setConfirm] = useState(null); // { row, typed }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setRows(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (!user.is_support_admin) {
      nav("/app", { replace: true });
      return;
    }
    // Defer the state-changing load() to a microtask so eslint's
    // react-hooks/set-state-in-effect rule is happy and React's render is
    // never blocked by the synchronous setLoading(true) inside load().
    queueMicrotask(load);
     
  }, [user]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        r.email?.toLowerCase().includes(needle) ||
        r.full_name?.toLowerCase().includes(needle) ||
        r.discipline?.toLowerCase().includes(needle) ||
        r.id?.toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const doDelete = async () => {
    if (!confirm || confirm.typed !== confirm.row.email) return;
    setDeleting(confirm.row.id);
    try {
      const { data } = await api.delete(`/admin/users/${confirm.row.id}`);
      const bits = [];
      if (data.stripe_subscription_cancelled) bits.push("subscription canceled");
      if (data.stripe_customer_deleted) bits.push("Stripe customer removed");
      const total = Object.values(data.counts || {}).reduce((s, n) => s + (n || 0), 0);
      bits.push(`${total} records cleared`);
      toast.success(`Deleted ${confirm.row.email} — ${bits.join(", ")}`);
      setRows((prev) => prev.filter((r) => r.id !== confirm.row.id));
      setConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-8 h-8 text-zinc-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => nav("/app")}
              className="p-2 rounded-md hover:bg-zinc-100"
              data-testid="admin-users-back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <p className="font-heading text-lg tracking-tight leading-none">User accounts</p>
              <p className="text-xs text-zinc-500 mt-0.5">{rows.length} total · admin only</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="admin-users-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-8">
        <div className="mb-5 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Search by email, name, discipline, or user id…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="admin-users-search"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-xl p-12 text-center" data-testid="admin-users-empty">
            <UsersIcon className="w-8 h-8 mx-auto text-zinc-400 mb-3" />
            <p className="text-zinc-600">No users match that search.</p>
          </div>
        ) : (
          <div className="border border-zinc-200 rounded-xl bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left text-xs uppercase tracking-[0.12em] text-zinc-500">
                    <th className="px-4 py-3 font-medium">User</th>
                    <th className="px-4 py-3 font-medium">Discipline</th>
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Signed up</th>
                    <th className="px-4 py-3 font-medium">PHI</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/60" data-testid={`admin-user-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {r.is_support_admin && (
                            <span title="Admin">
                              <ShieldCheck className="w-4 h-4 text-emerald-600" />
                            </span>
                          )}
                          <div>
                            <p className="font-medium text-zinc-900">{r.full_name || "—"}</p>
                            <p className="text-xs text-zinc-500">{r.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-700">{r.discipline || "—"}</td>
                      <td className="px-4 py-3 text-zinc-700">
                        {r.plan ? r.plan.replace(/_/g, " ") : <span className="text-zinc-400">— no plan</span>}
                        {r.org_id && <span className="block text-xs text-zinc-400 mt-0.5">org member</span>}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={r.subscription_status} />
                      </td>
                      <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3 text-zinc-700 whitespace-nowrap text-xs">
                        {r.patient_count} patients · {r.session_count} sessions
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirm({ row: r, typed: "" })}
                          disabled={r.is_support_admin || deleting === r.id}
                          className={r.is_support_admin ? "" : "text-red-700 border-red-200 hover:bg-red-50"}
                          data-testid={`admin-delete-${r.id}`}
                        >
                          {deleting === r.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Delete confirmation modal */}
      {confirm && (
        <div
          className="fixed inset-0 z-50 bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !deleting && setConfirm(null)}
          data-testid="admin-delete-confirm"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-heading text-xl tracking-tight text-zinc-900">Delete this user?</h3>
                <p className="text-sm text-zinc-600 mt-1">This permanently removes:</p>
              </div>
            </div>
            <ul className="space-y-1.5 text-sm text-zinc-700 ml-13 list-disc list-inside mb-5 bg-zinc-50 rounded-lg p-4">
              <li>The account ({confirm.row.email})</li>
              <li>{confirm.row.patient_count} patient cards (encrypted notes)</li>
              <li>{confirm.row.session_count} therapy session records</li>
              <li>All favorites, translations, audit history, support tickets</li>
              {confirm.row.stripe_subscription_id && <li>Active Stripe subscription (canceled)</li>}
              {confirm.row.stripe_customer_id && <li>Stripe customer record</li>}
              {confirm.row.org_role === "admin" && <li className="text-red-700"><ShieldAlert className="w-3.5 h-3.5 inline -mt-0.5" /> This user owns an organization — members will become orphaned</li>}
            </ul>
            <p className="text-sm text-zinc-700 mb-2">
              Type <span className="font-mono bg-zinc-100 px-1.5 py-0.5 rounded text-xs">{confirm.row.email}</span> to confirm:
            </p>
            <Input
              autoFocus
              value={confirm.typed}
              onChange={(e) => setConfirm((c) => ({ ...c, typed: e.target.value }))}
              placeholder={confirm.row.email}
              className="mb-5"
              data-testid="admin-delete-confirm-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirm(null)} disabled={!!deleting} data-testid="admin-delete-cancel">
                Cancel
              </Button>
              <Button
                onClick={doDelete}
                disabled={confirm.typed !== confirm.row.email || !!deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="admin-delete-confirm-button"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete forever
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
